/**
 * Spec 089e — Settings → Integrations → Backfill history subsection.
 *
 * Visible only to users with orbit:admin. Non-admins don't see it at all.
 *
 * States:
 *   - idle        — radio buttons (30/90/180/365/480) + Preview button
 *   - previewed   — show estimate + Confirm / Cancel buttons
 *   - in_progress — progress bar polled every 30s
 *   - error       — show message + retry
 *
 * Polling: while `in_progress`, GET .../backfill/status every 30s. Stops when
 * the panel unmounts or status returns in_progress=false.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { apiFetch } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';
import { useToast } from '../hooks/useToast.js';

interface BackfillPreview {
  target_date: string;
  estimated_chunks: number;
  estimated_units: number;
  request_token: string;
  days: number;
}

interface BackfillStatus {
  in_progress: boolean;
  target_date: string | null;
  progress_date: string | null;
  percent_complete: number | null;
}

type SectionState =
  | { kind: 'idle' }
  | { kind: 'previewed'; preview: BackfillPreview }
  | { kind: 'in_progress'; status: BackfillStatus }
  | { kind: 'error'; message: string };

interface Props {
  tenantId: string;
  brandId: string;
}

const ALLOWED_DAYS = [30, 90, 180, 365, 480] as const;
type AllowedDays = (typeof ALLOWED_DAYS)[number];

const DAYS_LABELS: Record<AllowedDays, string> = {
  30: 'Last 30 days',
  90: 'Last 90 days',
  180: 'Last 180 days',
  365: 'Last 365 days',
  480: 'Last 16 months (~480 days)',
};

const POLL_INTERVAL_MS = 30_000;

export function SearchConsoleBackfillSection({ tenantId, brandId }: Props) {
  const [days, setDays] = useState<AllowedDays>(90);
  const [state, setState] = useState<SectionState>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStatus = useCallback(async (): Promise<BackfillStatus | null> => {
    const res = await apiFetch(
      `/api/v1/tenants/${tenantId}/brands/${brandId}/search-console/backfill/status`,
    );
    if (!res.ok) return null;
    return (await res.json()) as BackfillStatus;
  }, [tenantId, brandId]);

  // Initial status check + poll setup.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const status = await fetchStatus();
      if (cancelled) return;
      if (status?.in_progress) {
        setState({ kind: 'in_progress', status });
        pollTimerRef.current = setTimeout(() => void tick(), POLL_INTERVAL_MS);
      } else {
        // If we were in progress and now complete, surface a success toast.
        setState((prev) => {
          if (prev.kind === 'in_progress') {
            toast.addToast('Backfill complete — rolling sync resumed', 'success');
          }
          return { kind: 'idle' };
        });
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [fetchStatus, toast]);

  const handlePreview = useCallback(async () => {
    setBusy(true);
    try {
      const preview = await apiPost<BackfillPreview>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/search-console/backfill`,
        { days },
      );
      setState({ kind: 'previewed', preview });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState({ kind: 'error', message: msg });
    } finally {
      setBusy(false);
    }
  }, [tenantId, brandId, days]);

  const handleConfirm = useCallback(async () => {
    if (state.kind !== 'previewed') return;
    setBusy(true);
    try {
      await apiPost<{ queued: boolean; backfill_target_date: string }>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/search-console/backfill/confirm`,
        {
          days: state.preview.days,
          request_token: state.preview.request_token,
        },
      );
      toast.addToast('Backfill queued — worker will start on the next tick', 'success');
      // Optimistically transition to in_progress so the user sees the bar.
      const status = await fetchStatus();
      if (status?.in_progress) {
        setState({ kind: 'in_progress', status });
        pollTimerRef.current = setTimeout(async () => {
          const next = await fetchStatus();
          if (next?.in_progress) {
            setState({ kind: 'in_progress', status: next });
          } else {
            setState({ kind: 'idle' });
          }
        }, POLL_INTERVAL_MS);
      } else {
        setState({ kind: 'idle' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState({ kind: 'error', message: msg });
    } finally {
      setBusy(false);
    }
  }, [state, tenantId, brandId, fetchStatus, toast]);

  const handleCancel = useCallback(() => {
    setState({ kind: 'idle' });
  }, []);

  return (
    <div data-testid="gsc-backfill-section" className="rounded-md border border-slate-200 bg-slate-50/40 p-4">
      <div className="flex items-center gap-2">
        <Icon name="bx-history" className="text-violet-600" />
        <h4 className="text-sm font-semibold text-slate-900">Backfill history</h4>
        <span className="ml-1 inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium capitalize text-violet-700">
          Admin
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Pull historical Google Search Console data into GEO Observatory. One 14-day chunk
        per worker tick (~5 min apart). Existing rolling 7-day sync pauses while
        the backfill runs and resumes automatically when it completes.
      </p>

      {state.kind === 'idle' && (
        <div className="mt-3 space-y-3">
          <fieldset className="space-y-1.5">
            <legend className="text-xs font-medium text-slate-700">Date range</legend>
            {ALLOWED_DAYS.map((d) => (
              <label key={d} className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="radio"
                  name="gsc-backfill-days"
                  value={d}
                  checked={days === d}
                  onChange={() => setDays(d)}
                  data-testid={`gsc-backfill-days-${d}`}
                />
                {DAYS_LABELS[d]}
              </label>
            ))}
          </fieldset>
          <Button variant="primary" onClick={() => void handlePreview()} disabled={busy} data-testid="gsc-backfill-preview-btn">
            {busy ? <Spinner /> : <Icon name="bx-history" />}
            Preview cost
          </Button>
        </div>
      )}

      {state.kind === 'previewed' && (
        <div className="mt-3 space-y-3">
          <div
            data-testid="gsc-backfill-preview"
            className="rounded-md border border-violet-200 bg-violet-50/60 p-3 text-xs text-slate-700"
          >
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
              <dt className="font-medium text-slate-600">Target date:</dt>
              <dd data-testid="gsc-backfill-target-date">{state.preview.target_date}</dd>
              <dt className="font-medium text-slate-600">Estimated chunks:</dt>
              <dd data-testid="gsc-backfill-chunks">{state.preview.estimated_chunks} × 14-day windows</dd>
              <dt className="font-medium text-slate-600">Estimated API calls:</dt>
              <dd>
                {state.preview.estimated_units}
                <span className="ml-1 text-slate-400">(Google Search Console — free)</span>
              </dd>
            </dl>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => void handleConfirm()} disabled={busy} data-testid="gsc-backfill-confirm-btn">
              {busy ? <Spinner /> : <Icon name="bx-check-circle" />}
              Confirm backfill
            </Button>
            <Button variant="ghost" onClick={handleCancel} disabled={busy} data-testid="gsc-backfill-cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {state.kind === 'in_progress' && state.status.target_date && state.status.progress_date && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>
              Backfilling history from {state.status.target_date} to {state.status.progress_date}
            </span>
            <span data-testid="gsc-backfill-percent" className="font-medium text-slate-700">
              {state.status.percent_complete ?? 0}%
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              data-testid="gsc-backfill-progress-bar"
              className="h-full rounded-full bg-violet-500 transition-all"
              style={{ width: `${state.status.percent_complete ?? 0}%` }}
            />
          </div>
        </div>
      )}

      {state.kind === 'error' && (
        <div
          data-testid="gsc-backfill-error"
          className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800"
        >
          <Icon name="bx-error" className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Backfill request failed</p>
            <p className="mt-0.5">{state.message}</p>
            <button
              type="button"
              onClick={() => setState({ kind: 'idle' })}
              className="mt-2 text-red-700 underline hover:text-red-800"
            >
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
